import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { LoanActions, SettingsActions } from '@portal/actions';

import Loans from '@portal/components/Loans/Loans';
import PanelContentBreadcrumb from '@portal/components/PanelContentBreadcrumb/PanelContentBreadcrumb';
import PanelContentHeader from '@portal/components/PanelContentHeader/PanelContentHeader';
import PreApprovedLoans from '@portal/components/PreApprovedLoans/PreApprovedLoans';

import { getRouteStackPath } from '@portal/config/routes';
import { loan } from '@portal/config/app.json';
import useReduxState from '@portal/hooks/useReduxState';
import {
  PaymentAccountStatus,
  PaymentAccountType,
} from '@portal/models/enumerators/paymentAccount';
import { translate } from '@portal/services/i18n';
import { getPaymentAccountByType } from '@portal/utils/paymentAccount';
import { useNavigation } from '@portal/hooks/useNavigation';

enum NewProductTypeEnum {
  CREDIT = 1,
}

const LINK_TO_HOME_PAGE = 'LINK_TO_HOME_PAGE';

const LoanPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();

  const {
    user: {
      me: {
        account: { paymentAccounts, settings },
      },
      me: loggedUser,
    },
    loan: { preApprovedLoans, loans },
    settings: { keys },
  } = useReduxState();

  const paymentAccount = getPaymentAccountByType(
    loggedUser,
    PaymentAccountType.ACCOUNT
  );

  const handleOpenLink = () => {
    const link = keys?.find(
      (key) => key && Object.hasOwn(key, LINK_TO_HOME_PAGE)
    );

    if (link) {
      window.open(link[LINK_TO_HOME_PAGE], '_blank');
    }
  };

  useEffect(() => {
    if (loan.enableAvailableSoon) {
      dispatch(SettingsActions.getSettingsKeys([LINK_TO_HOME_PAGE]));
    }
  }, [dispatch]);

  useEffect(() => {
    const hasLoanIntention = settings?.find((setting) =>
      setting?.value?.includes(String(NewProductTypeEnum.CREDIT))
    );

    if (loan.enableAvailableSoon && !hasLoanIntention) {
      dispatch(LoanActions.saveLoanIntention(NewProductTypeEnum.CREDIT));
    }

    if (
      !loan.enableAvailableSoon &&
      !paymentAccounts.some(
        (paymentAccount) =>
          paymentAccount.type === PaymentAccountType.LOAN &&
          paymentAccount.status === PaymentAccountStatus.APPROVED
      )
    ) {
      navigate(getRouteStackPath('LOAN', 'REGISTER'));
    }
  }, [navigate, paymentAccounts, dispatch, settings]);

  useEffect(() => {
    if (!loan.enableAvailableSoon) {
      dispatch(
        LoanActions.getPreApprovedLoans(
          !preApprovedLoans || preApprovedLoans.length <= 0
        )
      );
      dispatch(LoanActions.getLoans(!loans || loans.length <= 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return (
    <div className="loan">
      <PanelContentBreadcrumb
        items={[
          {
            active: !!paymentAccount,
            title: translate('PAGES.HOME.PAY.BREADCRUMB.HOME'),
            url: `${getRouteStackPath('HOME', 'HOME_PAGE')}`,
          },
          {
            active: false,
            title: translate('PAGES.LOAN.TITLE'),
          },
        ]}
      />
      <PanelContentHeader
        pageTitle={translate('PAGES.LOAN.LOAN_PAGE.TITLE')}
        pageDescription={translate('PAGES.LOAN.LOAN_PAGE.DESCRIPTION')}
      />
      {loan.enableAvailableSoon ? (
        <div>
          <p className="loan__container__available-soon">
            {translate('PAGES.LOAN.AVAILABLE_SOON.TITLE')}
          </p>
          <p className="loan__container__available-soon__description">
            {translate('PAGES.LOAN.AVAILABLE_SOON.DESCRIPTION')}
            <button
              className="loan__container__available-soon__link"
              onClick={handleOpenLink}
            >
              {translate('PAGES.LOAN.AVAILABLE_SOON.LINK')}
            </button>
          </p>
        </div>
      ) : (
        <>
          {preApprovedLoans && preApprovedLoans.length > 0 ? (
            <div className="loan__container__pre-approved-loans">
              <PreApprovedLoans />
            </div>
          ) : null}

          {loans && loans.length > 0 ? (
            <div className="loan__container__loans">
              <Loans loans={loans} />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default LoanPage;
